import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Observable } from 'rxjs';
import { finalize, catchError, map, switchMap, shareReplay } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { PreReadService, QuestionDefinition } from 'src/app/core/services/pre-read.service';
import {
  buildAnswerPayloadForStep,
  CaseEvent,
  DeadlineEntry,
  displayIssueTitle,
  DOCUMENT_USER_TAG_MAP,
  emptyPreReadAnswers,
  hydratePreReadAnswers,
  OpposingParty,
  PERSONAL_DOC_INDEX,
  PreReadAnswers,
  QUESTION_CODES,
  SUPPORTING_DOC_CATEGORY_MAP,
  SupportingDocument,
  toBackendCode,
  toBackendCurrentStep
} from 'src/app/core/models/pre-read.model';
import { QUESTIONNAIRE_TOTAL_STEPS, QUESTIONS, QuestionConfig } from '../questions.config';
import { countWords, truncateToWordLimit } from 'src/app/shared/utils/word-limit.util';
import { applySpokenPunctuationCommands } from 'src/app/shared/utils/voice-punctuation.util';
import { WordLimitTrimEvent } from 'src/app/shared/directives/word-limit-input.directive';

import { ShareLegalInformationDialogComponent } from '../../share-legal-information-dialog/share-legal-information-dialog.component';


export interface LegalInformationDialogData {
  preReadId?: string;
  readOnly?: boolean;
 
  legalFinanceCategoryId?: string | null;
}

type WizardMode = 'wizard' | 'review' | 'success';

const DOCUMENTS_UI_STEP = 6;

/**
 * Q5 "Aug 25, 2026" display format for the event/deadline mat-datepicker
 * inputs (was previously falling back to the app-wide date format, which
 * rendered as "25/08/2026"). Only affects how the date is *displayed* —
 * the underlying stored value/API payload date format is unchanged.
 */
const LI_DATE_DISPLAY_FORMATS = {
  parse: {
    dateInput: { year: 'numeric', month: 'short', day: 'numeric' }
  },
  display: {
    dateInput: { year: 'numeric', month: 'short', day: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

@Component({
  selector: 'app-legal-information-questionnaire',
  templateUrl: './legal-information-questionnaire.component.html',
  styleUrls: ['./legal-information-questionnaire.component.css'],
  standalone: false,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: MAT_DATE_FORMATS, useValue: LI_DATE_DISPLAY_FORMATS }
  ]
})
export class LegalInformationQuestionnaireComponent implements OnInit, OnDestroy {

  questions: QuestionConfig[] = QUESTIONS;
  totalSteps = QUESTIONNAIRE_TOTAL_STEPS;

  mode: WizardMode = 'wizard';
  currentStepIndex = 0;
  answers: PreReadAnswers = emptyPreReadAnswers();
  preReadId: string | null = null;
  legalInfoStatus: string = 'draft';
  readOnly = false;

  /** Q3 — true once the user has attempted to advance (Save & Next or
   *  Save for Later) while the Opposing Party step was invalid. Lets the
   *  Phone/Email inline errors appear immediately on that attempt instead
   *  of requiring a manual blur first. Reset on every step change. */
  partyValidationAttempted = false;

  private legalFinanceCategoryId: string | null = null;
  
  private legalFinanceCategoryId$: Observable<string | null> | null = null;
  private activeUploadCount = 0;

  loading = false;
  saving = false;
  submitting = false;
  uploading = false;

  questionsLoading = false;
  questionsError = false;

  // Q2 / Q7 / Q8 "Add more" dropdown state — remaining (not-yet-selected) options per step
  otherSpecifyText: { [step: number]: string } = {};

 
  openDropdownStep: number | null = null;
  dropdownSearch: { [step: number]: string } = {};

  // Q6 document-type combobox — which document row's dropdown is open
  // (mutually exclusive with openDropdownStep; shares the same panel
  // positioning/scroll-lock machinery below).
  openDocDropdownIndex: number | null = null;

 
  msPanelStyle: { [key: string]: string } = {};
  msOpenUpward = false;
  private msTriggerEl: HTMLElement | null = null;
  private readonly msReposition = () => this.updateDropdownPosition();
  
  iDocuments:any[]=[];

  docLibraryOpen = false;
  docLibraryLoading = false;
  docLibrarySearch = '';
  librarySelection = new Set<string>();

  constructor(
    private dialogRef: MatDialogRef<LegalInformationQuestionnaireComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LegalInformationDialogData,
    private preReadService: PreReadService,
    private auth: AuthService,
    private toastr: ToastrService,
    private dialog: MatDialog,

    private ngZone?: NgZone
  ) { }

  ngOnInit(): void {
    this.readOnly = !!this.data?.readOnly;
    this.legalFinanceCategoryId = this.data?.legalFinanceCategoryId ?? null;
    if (!this.readOnly) {
      
      this.resolveLegalFinanceCategoryId().subscribe();
    }

    this.loadQuestions(() => {
      if (this.data?.preReadId) {
        this.loadExistingPreRead(this.data.preReadId);
      }
     
    });
    this.getDocuments();
  }

  private loadQuestions(onDone: () => void): void {
    this.questionsLoading = true;
    this.questionsError = false;
    this.preReadService.getQuestions().pipe(
      finalize(() => this.questionsLoading = false)
    ).subscribe({
      next: (apiQuestions) => {
        this.applyDynamicQuestionOrder(apiQuestions);
        onDone();
      },
      error: () => this.questionsError = true
    });
  }

  retryLoadQuestions(): void {
    this.loadQuestions(() => {
      if (this.data?.preReadId && !this.answers.hasCaseTitle && this.currentStepIndex === 0) {
        this.loadExistingPreRead(this.data.preReadId);
      }
    });
  }

  private applyDynamicQuestionOrder(apiQuestions: QuestionDefinition[]): void {
    if (!apiQuestions?.length) { return; }
    const byCode = new Map(apiQuestions.map(q => [q.question_code, q]));
    const ordered = QUESTIONS
      .filter(q => byCode.has(q.questionCode))
      .sort((a, b) => {
        const stepA = byCode.get(a.questionCode)?.step ?? a.step;
        const stepB = byCode.get(b.questionCode)?.step ?? b.step;
        return stepA - stepB;
      });
    if (ordered.length) {
      this.questions = ordered;
      this.totalSteps = ordered.length;
    }
  }

  private loadExistingPreRead(preReadId: string): void {
    this.preReadId = preReadId;
    this.loading = true;
    this.preReadService.getById(preReadId).pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: (preRead) => {
       this.legalInfoStatus = (preRead?.status || 'draft').toLowerCase(); // Store the current Legal Information status

        if (preRead?.answers) {
          this.answers = hydratePreReadAnswers(
            preRead.answers,
            (uiStep, code) => this.optionLabelForBackendCode(uiStep, code)
          );
        }
   
        if (typeof preRead?.current_step === 'number' && !this.readOnly) {
          const uiStep = preRead.current_step;
          this.currentStepIndex = Math.min(Math.max(uiStep - 1, 0), this.totalSteps - 1);
        }
        if (this.readOnly) {
          this.mode = 'review';
        }
      },
      error: () => {
        this.toastr.error('Could not load this Legal Information entry.');
        this.dialogRef.close();
      }
    });
  }

  private ensurePreReadId(): Observable<string> {
    if (this.preReadId) { return of(this.preReadId); }
    return this.preReadService.createDraft().pipe(
      map(res => {
        this.preReadId = res.pre_read_id;
        return this.preReadId as string;
      })
    );
  }

  private optionLabelForBackendCode(uiStep: number, code: string): string | undefined {
    const q = this.questions.find(qq => qq.step === uiStep) || QUESTIONS.find(qq => qq.step === uiStep);
    return (q?.options || []).find(label => toBackendCode(label) === code);
  }

  private resolveLegalFinanceCategoryId(): Observable<string | null> {
    if (this.legalFinanceCategoryId) { return of(this.legalFinanceCategoryId); }
    if (!this.legalFinanceCategoryId$) {
      this.legalFinanceCategoryId$ = this.auth.get_tags(PERSONAL_DOC_INDEX).pipe(
        map((res: any) => {
          const categories = res?.data?.categories || [];
          const match = categories.find((c: any) => (c.tagname || '').toLowerCase() === 'legalfinance');
          this.legalFinanceCategoryId = match?.id || null;
          return this.legalFinanceCategoryId;
        }),
        catchError(() => {
          this.legalFinanceCategoryId = null;
          return of(null);
        }),
        shareReplay(1)
      );
    }
    return this.legalFinanceCategoryId$;
  }

  // ---------- Step helpers ----------

  get currentQuestion(): QuestionConfig {
    return this.questions[this.currentStepIndex];
  }

  get progressPercent(): number {
    return ((this.currentStepIndex + 1) / this.totalSteps) * 100;
  }

  textValue(key: string): string {
    return (this.answers as any)[key] || '';
  }

  setTextValue(key: string, value: string): void {
    (this.answers as any)[key] = this.truncateToWordLimit(value, this.currentQuestion?.wordLimit);
  }

  /** Q1 case title: same word-limit enforcement as setTextValue, kept
   *  separate since caseTitle is its own field on `answers`, not driven by
   *  a dynamic `currentQuestion.key`. */
  setCaseTitle(value: string): void {
    this.answers.caseTitle = this.truncateToWordLimit(value, this.currentQuestion?.wordLimit);
  }

  /** Q5 event text: enforce the same 50-word cap the counter already shows. */
  setEventText(evt: CaseEvent, value: string): void {
    evt.event = this.truncateToWordLimit(value, 50);
  }

  /** Q9 deadline notes: the "Other" deadline type carries a 50-word cap
   *  (it doubles as that type's required free-text field); the remaining
   *  deadline types' Notes are optional but still capped at the PRD's
   *  150-word limit, with the same live counter as everywhere else. */
  setDeadlineNotes(type: string, value: string): void {
    const entry = this.deadlineFor(type);
    if (!entry) { return; }
    entry.notes = this.truncateToWordLimit(value, type === 'Other' ? 50 : 150);
  }

  /** Q6 document name: enforce the same 50-word cap the counter already shows.
   *  If the row is currently showing a duplicate-name error, editing the name
   *  clears that error immediately (the row returns to normal) so the user
   *  isn't forced to hit Retry — the corrected name is what gets sent on the
   *  next upload/rename attempt (see uploadDocumentAndTrack/renameDocument). */
  setDocumentName(doc: SupportingDocument, value: string): void {
    doc.documentName = this.truncateToWordLimit(value, 50);
    if (this.isDuplicateNameError(doc)) {
      doc.uploadError = undefined;
    }
  }

  /** Q6: whether a document's current uploadError is specifically a
   *  duplicate-name conflict from the server (as opposed to a network/other
   *  upload failure). Message text is server-driven, so this matches
   *  case-insensitively rather than on an exact string. */
  isDuplicateNameError(doc: SupportingDocument): boolean {
    return /already\s*exist/i.test(doc.uploadError || '');
  }

  /** Q6 "Other" document category free text: enforce the same 50-word cap
   *  the counter already shows. */
  setDocOtherCategoryText(doc: SupportingDocument, value: string): void {
    doc.otherCategoryText = this.truncateToWordLimit(value, 50);
  }

  wordCount(text: string): number {
    return countWords(text);
  }

  /** Whether a field's live word counter should switch into its
   *  "at limit" visual state (red text on `.li-word-count`). Purely a
   *  display helper — the actual hard-stop enforcement lives in
   *  WordLimitInputDirective / truncateToWordLimit; this just mirrors
   *  that state for the UI. */
  isAtWordLimit(text: string, limit?: number | null): boolean {
    return !!limit && this.wordCount(text) >= limit;
  }

  // ---------- Word-limit trim notice (paste / dictation / typing at cap) ----------

  wordLimitNoticeMessage = '';
  private wordLimitNoticeTimeout: any = null;


  onWordLimitTrimmed(event: WordLimitTrimEvent): void {
    const message = event.source === 'type'
      ? `Word limit reached — ${event.limit}/${event.limit} words.`
      : `Pasted text was trimmed to ${event.limit} words.`;

    if (event.source !== 'type') {
      this.toastr.info(message);
    }

    this.wordLimitNoticeMessage = message;
    if (this.wordLimitNoticeTimeout) { clearTimeout(this.wordLimitNoticeTimeout); }
    this.wordLimitNoticeTimeout = setTimeout(() => {
      this.wordLimitNoticeMessage = '';
    }, 4000);
  }

  
  private truncateToWordLimit(value: string, limit?: number): string {
    return truncateToWordLimit(value, limit);
  }

  private readonly dateStringToDateCache = new Map<string, Date>();

  dateStringToDate(value: string | null | undefined): Date | null {
    if (!value) { return null; }
    const cached = this.dateStringToDateCache.get(value);
    if (cached) { return cached; }
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) { return null; }
    this.dateStringToDateCache.set(value, parsed);
    return parsed;
  }

  dateToDateString(value: Date | null): string {
    if (!value) { return ''; }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

 
  isStepValid(q: QuestionConfig): boolean {
    switch (q.type) {
      case 'text-yesno':
        // Q1
        if (this.answers.hasCaseTitle === null) { return false; }
        if (this.answers.hasCaseTitle) { return !!(this.answers.caseTitle || '').trim(); }
        return true;

      case 'chips-yesno': {
        // Q2 / Q8 share this type; Q9 (deadlines) is handled below.
        if (q.step === 9) {
          if (this.answers.knowsDeadlines === null) { return false; }
          if (!this.answers.knowsDeadlines) { return true; }
          if (!this.answers.deadlines.length) { return false; }
          return this.answers.deadlines.every(d => {
            if (!d.date) { return false; }
            // PRD Q9 "Other": dedicated 50-word free-text field — Notes
            // doubles as that field for the Other deadline type only.
            if (d.type === 'Other') {
              const wc = this.wordCount(d.notes || '');
              return wc > 0 && wc <= 50;
            }
            return true;
          });
        }
        const known = this.getBinaryAnswer(q);
        if (known === null) { return false; }
        if (!known) { return true; }
        return this.isChipSelectionValid(q);
      }

      case 'chips-radio': {
        // Q7
        if (this.answers.wantsOutcomeOptions === null) { return false; }
        if (!this.answers.wantsOutcomeOptions) { return true; }
        return this.isChipSelectionValid(q);
      }

      case 'parties':
        // Q3 — Name/Phone/Role required, Email optional; Phone must be
        // exactly 10 digits and, when provided, Email must be a valid
        // address (see isPartyPhoneValid / isPartyEmailValid).
        if (this.answers.knowsOpposingParty === null) { return false; }
        if (!this.answers.knowsOpposingParty) { return true; }
        return this.answers.opposingParties.length > 0 &&
          this.answers.opposingParties.every(p =>
            !!p.name?.trim() &&
            !!p.phone?.trim() && this.isPartyPhoneValid(p) &&
            !!p.role?.trim() &&
            this.isPartyEmailValid(p));

      case 'textarea':
        if (q.required) { return this.wordCount((this.answers as any)[q.key]) > 0; }
        return true;

      case 'events':
        // Q5 — no events added yet preserves the existing "not required"
        // behavior (every() on an empty array is true). Once a row exists,
        // its Date and Event are required; Time stays optional and is
        // never checked here.
        return this.answers.events.every(e => this.isEventValid(e));

      case 'documents':
        // Q6 — also blocks on any row with an unresolved upload error
        // (including a duplicate-name conflict) so Save & Next / Save for
        // Later can't be used to skip past a document that never finished
        // uploading successfully.
        if (this.answers.hasDocuments === null) { return false; }
        if (!this.answers.hasDocuments) { return true; }
        return this.answers.documents.length > 0 &&
          this.answers.documents.every(d => !!d.documentType && this.isDocOtherValid(d) && !d.uploadError);

      default:
        return true;
    }
  }
  
  
  /** Shared check for the Q2/Q7/Q8 chip multi-select + "Other" text combo. */
  private isChipSelectionValid(q: QuestionConfig): boolean {
    const selected = this.selectedOptions(q);
    if (!selected.length) { return false; }
    if (this.isOtherSelected(q)) {
      const text = this.otherText(q).trim();
      if (!text) { return false; }
      if (!this.isOtherTextValid(q)) { return false; }
    }
    return true;
  }

 
  get nextDisabledReason(): string {
    if (this.isStepValid(this.currentQuestion)) { return ''; }
    const q = this.currentQuestion;
    if (q.type === 'documents' && this.answers.documents.some(d => this.isDuplicateNameError(d))) {
      return 'Please change the duplicate document name before saving.';
    }
    const binaryUnanswered =
      (q.type === 'text-yesno' && this.answers.hasCaseTitle === null) ||
      (q.type === 'parties' && this.answers.knowsOpposingParty === null) ||
      (q.type === 'documents' && this.answers.hasDocuments === null) ||
      (q.type === 'chips-radio' && this.answers.wantsOutcomeOptions === null) ||
      (q.type === 'chips-yesno' && q.step !== 9 && this.getBinaryAnswer(q) === null) ||
      (q.step === 9 && this.answers.knowsDeadlines === null);
    return binaryUnanswered
      ? 'Select an option to continue'
      : 'Complete the required information to continue';
  }

  next(): void {
    if (!this.isStepValid(this.currentQuestion)) {
      this.partyValidationAttempted = true;
      this.toastr.warning('Please complete the required fields before continuing.');
      return;
    }
    const uiStep = this.currentStepIndex + 1;

    if (uiStep === DOCUMENTS_UI_STEP) {
      // Documents step: upload-then-patch flow (PRD Part 15), not the
      // generic per-step patch below.
      this.persistDocumentsStep(() => this.advanceStep());
      return;
    }

    this.persist(uiStep, () => this.advanceStep());
  }

  private advanceStep(): void {
    this.partyValidationAttempted = false;
    if (this.currentStepIndex >= this.totalSteps - 1) {
      this.mode = 'review';
    } else {
      this.currentStepIndex++;
    }
  }

  previous(): void {
    this.partyValidationAttempted = false;
    if (this.mode === 'review') {
      this.mode = 'wizard';
      this.currentStepIndex = this.totalSteps - 1;
      return;
    }
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    }
    this.reviewExpanded['documents'] = false;
  }


  private persistAllAnswered(uiStepCount: number, onSuccess: () => void): void {
    if (this.saving) { onSuccess(); return; }
    const answers: ReturnType<typeof buildAnswerPayloadForStep> = [];
    for (let step = 1; step <= uiStepCount; step++) {
      if (step === DOCUMENTS_UI_STEP) { continue; } // documents are saved separately (upload-then-patch)
      answers.push(...buildAnswerPayloadForStep(step, this.answers));
    }
    const backendStep = toBackendCurrentStep(uiStepCount);
    if (!answers.length) { onSuccess(); return; }

    this.saving = true;
    this.ensurePreReadId().pipe(
      switchMap(preReadId => this.preReadService.patchAnswers(preReadId, backendStep, answers)),
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => onSuccess(),
      error: () => this.toastr.error('Could not save your progress. Please try again.')
    });
  }

  /** Q3 guard for Save for Later — Next already blocks on isStepValid, but
   *  Save for Later persists every answered step without re-checking step
   *  validity, so a malformed Opposing Party phone/email could otherwise
   *  reach the API. Only checks phone/email *format* (not required-ness),
   *  so legitimate partial progress on other steps can still be saved;
   *  required-ness on the Opposing Party step itself is covered by
   *  saveForLaterDisabled below while that step is the current one. */
  private hasInvalidOpposingPartyData(): boolean {
    if (!this.answers.knowsOpposingParty) { return false; }
    return this.answers.opposingParties.some(p =>
      (!!p.phone && !this.isPartyPhoneValid(p)) || !this.isPartyEmailValid(p));
  }

  /** Single source of truth for the Save for Later button's disabled
   *  state — reuses the existing "must answer Q1 first" gate plus full
   *  current-step validity (via isStepValid, the exact same check Save &
   *  Next uses for every question type — not just Opposing Party/Documents,
   *  which were previously the only types re-checked here, leaving every
   *  other step type able to save while genuinely incomplete), plus the
   *  existing format-only guard for any already-entered party data
   *  regardless of which step is currently active. */
  get saveForLaterDisabled(): boolean {
    if (this.saving || this.uploading) { return true; }
    if (this.currentStepIndex === 0 && this.answers.hasCaseTitle === null) { return true; }
    if (!this.isStepValid(this.currentQuestion)) { return true; }
    return this.hasInvalidOpposingPartyData();
  }

  saveForLater(): void {
    if (this.saveForLaterDisabled) {
      if (this.currentQuestion.type === 'parties' && !this.isStepValid(this.currentQuestion)) {
        this.partyValidationAttempted = true;
        this.toastr.warning('Please complete the required Opposing Party fields before saving.');
      } else if (!this.isStepValid(this.currentQuestion)) {
        this.toastr.warning(this.nextDisabledReason || 'Please complete the required fields before saving.');
      } else if (this.hasInvalidOpposingPartyData()) {
        this.toastr.warning('Please correct the Opposing Party phone/email details before saving.');
      }
      return;
    }
    const uiStep = this.currentStepIndex + 1;
    const onDone = () => {
      this.toastr.success('Progress saved. You can continue this later from your Legal Information list.');
      this.dialogRef.close('refresh');
    };
    if (uiStep >= DOCUMENTS_UI_STEP && this.answers.hasDocuments !== null) {
      
      this.persistDocumentsStep(() => this.persistAllAnswered(uiStep, onDone));
    } else {
      this.persistAllAnswered(uiStep, onDone);
    }
  }

  cancel(): void {
    this.dialogRef.close('refresh');
  }

  editSection(step: number): void {
    this.partyValidationAttempted = false;
    this.mode = 'wizard';
    this.currentStepIndex = step - 1;
    this.reviewExpanded['documents'] = false;
  }

  

  private readonly reviewTextPreviewLength = 140;
  private readonly reviewListPreviewCount = 3;

  /** Tracks "Read more" expansion per long-text review field (e.g. 'incident', 'additional'). */
  reviewExpanded: { [key: string]: boolean } = {};

  isReviewTextLong(text: string | null | undefined): boolean {
    return (text || '').length > this.reviewTextPreviewLength;
  }

  reviewTextPreview(text: string | null | undefined): string {
    const value = text || '';
    if (value.length <= this.reviewTextPreviewLength) { return value; }
    return value.slice(0, this.reviewTextPreviewLength).trim() + '…';
  }

  isReviewExpanded(key: string): boolean {
    return !!this.reviewExpanded[key];
  }

  toggleReviewExpanded(key: string): void {
    this.reviewExpanded[key] = !this.reviewExpanded[key];
  }

  reviewVisibleEvents(): CaseEvent[] {
    return (this.answers.events || []).slice(0, this.reviewListPreviewCount);
  }

  reviewExtraEventsCount(): number {
    return Math.max(0, (this.answers.events || []).length - this.reviewListPreviewCount);
  }

  // reviewVisibleDocuments(): SupportingDocument[] {
  //   return (this.answers.documents || []).slice(0, this.reviewListPreviewCount);
  // }
  reviewVisibleDocuments(): SupportingDocument[] {
    const docs = this.answers.documents || [];
    return this.isReviewExpanded('documents')
      ? docs
      : docs.slice(0, this.reviewListPreviewCount);
  }

  reviewExtraDocumentsCount(): number {
    return Math.max(0, (this.answers.documents || []).length - this.reviewListPreviewCount);
  }

  /**
   * Formats a stored "YYYY-MM-DD" (optionally with a trailing time/offset,
   * e.g. Q5's combined "YYYY-MM-DDTHH:mm:ss+05:30") date string for display
   * on the Review screen, matching the documents module's date format
   * (documents.component.ts formatDate()) — e.g. "Aug 27, 2026" — instead
   * of showing the raw stored string. Only affects display; the underlying
   * stored value/API payload is unchanged. Parses the year/month/day
   * numerically rather than via `new Date(string)` so the displayed date
   * never shifts a day due to UTC parsing in a non-UTC timezone.
   */
  reviewFormatDate(dateStr: string | null | undefined): string {
    if (!dateStr) { return ''; }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (!match) { return dateStr; }
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (isNaN(date.getTime())) { return dateStr; }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /** Human-readable label for a single deadline entry, e.g. "Reply before Aug 20, 2026". */
  reviewDeadlineLabel(d: DeadlineEntry): string {
    const dateStr = this.reviewFormatDate(d.date);
    switch (d.type) {
      case 'Notice reply deadline': return dateStr ? `Reply before ${dateStr}` : 'Notice reply deadline';
      case 'Filing deadline': return dateStr ? `File before ${dateStr}` : 'Filing deadline';
      case 'Hearing date': return dateStr ? `Hearing on ${dateStr}` : 'Hearing date';
      case 'Limitation deadline': return dateStr ? `Limitation ends ${dateStr}` : 'Limitation deadline';
      default: return dateStr ? `${d.type}: ${dateStr}` : d.type;
    }
  }

 
  submit(): void {
    if (!this.preReadId) { return; }

    const answers: ReturnType<typeof buildAnswerPayloadForStep> = [];
    for (let step = 1; step <= this.totalSteps; step++) {
      if (step === DOCUMENTS_UI_STEP) {
        const documentIds = this.answers.documents
          .filter(d => d.uploaded && d.id)
          .map(d => d.id as string);
        answers.push(this.buildSupportingDocumentsAnswer(documentIds));
        continue;
      }
      answers.push(...buildAnswerPayloadForStep(step, this.answers));
    }

    this.submitting = true;
    this.preReadService.patchAnswers(this.preReadId, toBackendCurrentStep(this.totalSteps), answers).pipe(
      finalize(() => this.submitting = false)
    ).subscribe({
      //next: () => this.mode = 'success',
      next: (res: any) => {
        this.legalInfoStatus = (res?.status || 'draft').toLowerCase();
        this.mode = 'success';
      },
      error: () => this.toastr.error('Could not save your Legal Information. Please try again.')
    });
  }

  backToList(): void {
    this.dialogRef.close('refresh');
  }

  shareWithLawyer(): void {
    //if (!this.preReadId) { return; }
    if (!this.preReadId || this.legalInfoStatus !== 'completed') {
      return;
    }

    const title = displayIssueTitle({ issue_title: (this.answers.caseTitle || '').trim() });

    const ref = this.dialog.open(ShareLegalInformationDialogComponent, {
      data: {
        document: {
          id: this.preReadId,
          name: title,
          title,
          description: (this.answers.incidentDescription || '').trim(),
          // Stays an array — the dialog does data.document.issue_type.join(', '),
          // same as the list's share flow.
          issue_type: this.answers.issueTypes
        }
      },
      width: '700px',
      maxWidth: '92vw',
      panelClass: 'li-share-dialog-panel',
      disableClose: true,
      autoFocus: false
    });

    ref.afterClosed().subscribe((result: { refresh?: boolean; shared?: boolean } | undefined) => {
      if (result?.shared) {
        this.toastr.success('Legal information shared successfully.');
      }
      if (result?.refresh) {
        this.dialogRef.close('refresh');
        return;
      }
      // If the user just closed/cancelled the picker with no changes made,
      // stay on the success screen so they can still choose "Back to List"
      // or try sharing again.
    });
  }

  // ---------- Persistence (PRD Part 7 / Part 20 / Part 23) ----------


  private persist(uiStep: number, onSuccess: () => void): void {
    if (this.saving) { onSuccess(); return; }
    const answers = buildAnswerPayloadForStep(uiStep, this.answers);
    if (!answers.length) { onSuccess(); return; }

    this.saving = true;
    this.ensurePreReadId().pipe(
      switchMap(preReadId => this.preReadService.patchAnswers(preReadId, toBackendCurrentStep(uiStep), answers)),
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => onSuccess(),
      error: () => this.toastr.error('Could not save your progress.')
    });
  }

  // ---------- Q1: case title ----------

  setHasCaseTitle(value: boolean): void {
    this.answers.hasCaseTitle = value;
    if (!value) { this.answers.caseTitle = ''; }
  }

  // ---------- Q2 / Q7 / Q8: chip multi-select helpers ----------

  private listAndOtherFor(step: number): { list: string[]; otherKey: keyof PreReadAnswers } {
    switch (step) {
      case 2: return { list: this.answers.issueTypes, otherKey: 'issueTypeOther' };
      case 7: return { list: this.answers.expectedOutcomes, otherKey: 'expectedOutcomeOther' };
      case 8: return { list: this.answers.legalActions, otherKey: 'legalActionOther' };
      case 9: return { list: [], otherKey: 'additionalInformation' }; // handled separately (deadlines)
      default: return { list: [], otherKey: 'additionalInformation' };
    }
  }

  /** Backing boolean for the "chips-yesno" steps that share this type (Q2, Q8). */
  getBinaryAnswer(q: QuestionConfig): boolean | null {
    if (q.step === 2) { return this.answers.knowsIssueType; }
    if (q.step === 8) { return this.answers.legalActionTaken; }
    return null;
  }

  setBinaryAnswer(q: QuestionConfig, value: boolean): void {
    if (q.step === 2) { this.answers.knowsIssueType = value; }
    if (q.step === 8) { this.answers.legalActionTaken = value; }
  }

  selectedOptions(q: QuestionConfig): string[] {
    return this.listAndOtherFor(q.step).list;
  }

  remainingOptions(q: QuestionConfig): string[] {
    const selected = this.selectedOptions(q);
    const all = [...(q.options || []), ...(q.otherOptionLabel ? [q.otherOptionLabel] : [])];
    return all.filter(o => !selected.includes(o));
  }

  addOption(q: QuestionConfig, value: string): void {
    if (!value) { return; }
    const { list } = this.listAndOtherFor(q.step);
    if (!list.includes(value)) { list.push(value); }
  }

  removeOption(q: QuestionConfig, value: string): void {
    const { list } = this.listAndOtherFor(q.step);
    const idx = list.indexOf(value);
    if (idx > -1) { list.splice(idx, 1); }
    // PRD §16: removing the "Other" chip must hide + clear its free-text field.
    if (q.otherOptionLabel && value === q.otherOptionLabel) {
      this.setOtherText(q, '');
    }
  }

  isOtherSelected(q: QuestionConfig): boolean {
    return !!q.otherOptionLabel && this.selectedOptions(q).includes(q.otherOptionLabel);
  }

  otherText(q: QuestionConfig): string {
    const { otherKey } = this.listAndOtherFor(q.step);
    return (this.answers as any)[otherKey] || '';
  }

  setOtherText(q: QuestionConfig, value: string): void {
    const { otherKey } = this.listAndOtherFor(q.step);
    (this.answers as any)[otherKey] = this.truncateToWordLimit(value, q.wordLimit ?? 50);
  }

  /** PRD Part 12: "Other" free text is capped at 50 WORDS, not characters. */
  otherTextWordCount(q: QuestionConfig): number {
    return this.wordCount(this.otherText(q));
  }

  isOtherTextValid(q: QuestionConfig): boolean {
    const limit = q.wordLimit ?? 50;
    return this.otherTextWordCount(q) <= limit;
  }

  // ---------- Multi-select autocomplete combobox (PRD §15/16 — Q2, Q7, Q8, Q9) ----------


  toggleDropdown(step: number, event?: MouseEvent): void {
    if (this.openDropdownStep === step) {
      this.closeDropdown();
      return;
    }
    this.closeDocTypeDropdown();
    this.closeEventTimeDropdown();
    this.openDropdownStep = step;
    if (this.dropdownSearch[step] === undefined) {
      this.dropdownSearch[step] = '';
    }
    this.msTriggerEl = (event?.currentTarget as HTMLElement) || null;
    this.updateDropdownPosition();
    document.addEventListener('scroll', this.msReposition, true);
    window.addEventListener('resize', this.msReposition);
  }

  closeDropdown(): void {
    this.openDropdownStep = null;
    this.msTriggerEl = null;
    document.removeEventListener('scroll', this.msReposition, true);
    window.removeEventListener('resize', this.msReposition);
  }

  // ---------- Q6 document-type combobox — modernized single-select
  // dropdown (replaces the native <select>), reusing the same fixed-
  // position/scroll-lock panel logic as the multi-select above so it
  // scrolls internally and never gets clipped at the viewport edge. ----------

  toggleDocTypeDropdown(index: number, event?: MouseEvent): void {
    if (this.openDocDropdownIndex === index) {
      this.closeDocTypeDropdown();
      return;
    }
    this.closeDropdown();
    this.closeEventTimeDropdown();
    this.openDocDropdownIndex = index;
    this.msTriggerEl = (event?.currentTarget as HTMLElement) || null;
    this.updateDropdownPosition();
    document.addEventListener('scroll', this.msReposition, true);
    window.addEventListener('resize', this.msReposition);
  }

  closeDocTypeDropdown(): void {
    this.openDocDropdownIndex = null;
    this.msTriggerEl = null;
    document.removeEventListener('scroll', this.msReposition, true);
    window.removeEventListener('resize', this.msReposition);
  }

  isDocTypeDropdownOpen(index: number): boolean {
    return this.openDocDropdownIndex === index;
  }

  // selectDocumentType(doc: SupportingDocument, type: string): void {
  //   doc.documentType = type;
  //   this.closeDocTypeDropdown();
  // }
  selectDocumentType(doc: SupportingDocument, type: string): void {
    const changed = doc.documentType !== type;
    doc.documentType = type;
    this.closeDocTypeDropdown();

    // "Other" needs its free-text description first — don't fire yet,
    // the blur handler on the "Please specify" field below covers it.
    // if (changed && type !== 'Other') {
    //   this.persistDocumentTypeChange(doc);
    // }
  }

  private persistDocumentTypeChange(doc: SupportingDocument): void {
    if (!doc.uploaded || !doc.id) { return; }
    this.renameDocument(doc).subscribe(ok => {
      if (ok) {
        doc.originalDocumentType = doc.documentType;
        doc.originalOtherCategoryText = doc.otherCategoryText;
      }
    });
  }
  onOtherCategoryBlur(doc: SupportingDocument): void {
    if (doc.documentType !== 'Other') { return; }
    const text = (doc.otherCategoryText || '').trim();
    if (!text) { return; }
    const typeChanged = doc.originalDocumentType !== 'Other';
    const textChanged = text !== (doc.originalOtherCategoryText || '').trim();
    if (!typeChanged && !textChanged) { return; }
    this.persistDocumentTypeChange(doc);
  }

  private updateDropdownPosition(): void {
    if (!this.msTriggerEl) { return; }
    const rect = this.msTriggerEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const estimatedPanelHeight = 320;
    const gap = 6;
    const spaceBelow = viewportHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;

    this.msOpenUpward = spaceBelow < estimatedPanelHeight && spaceAbove > spaceBelow;

    const availableHeight = Math.max(160, Math.min(estimatedPanelHeight, this.msOpenUpward ? spaceAbove : spaceBelow));

    this.msPanelStyle = this.msOpenUpward
      ? {
          position: 'fixed',
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          bottom: `${viewportHeight - rect.top + gap}px`,
          top: 'auto',
          maxHeight: `${availableHeight}px`
        }
      : {
          position: 'fixed',
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          top: `${rect.bottom + gap}px`,
          bottom: 'auto',
          maxHeight: `${availableHeight}px`
        };
  }

  isDropdownOpen(step: number): boolean {
    return this.openDropdownStep === step;
  }

  getDropdownSearch(step: number): string {
    return this.dropdownSearch[step] || '';
  }

  setDropdownSearch(step: number, value: string): void {
    this.dropdownSearch[step] = value;
  }

  /** All selectable options for a chip question, including the "Other" entry. */
  allOptions(q: QuestionConfig): string[] {
    return [...(q.options || []), ...(q.otherOptionLabel ? [q.otherOptionLabel] : [])];
  }

  /** Options filtered by the per-step type-to-search query (case-insensitive). */
  filteredDropdownOptions(q: QuestionConfig): string[] {
    const term = this.getDropdownSearch(q.step).trim().toLowerCase();
    const all = this.allOptions(q);
    return term ? all.filter(o => o.toLowerCase().includes(term)) : all;
  }

  isOptionSelected(q: QuestionConfig, option: string): boolean {
    return this.selectedOptions(q).includes(option);
  }

 
  toggleOption(q: QuestionConfig, option: string): void {
    if (this.isOptionSelected(q, option)) {
      this.removeOption(q, option);
    } else {
      this.addOption(q, option);
      if (q.otherOptionLabel && option === q.otherOptionLabel) {
        this.closeDropdown();
      }
    }
  }

  /** Q9 deadline-type combobox (shares the same UI/CSS as the chip questions). */
  filteredDeadlineOptions(): string[] {
    const term = this.getDropdownSearch(9).trim().toLowerCase();
    return term ? this.deadlineTypes.filter(o => o.toLowerCase().includes(term)) : this.deadlineTypes;
  }

  // ---------- Q3: opposing parties ----------


  setKnowsOpposingParty(value: boolean): void {
    this.answers.knowsOpposingParty = value;
    if (value && !this.answers.opposingParties.length) {
      this.addParty();
    }
    if (!value) {
      this.answers.opposingParties = [];
    }
  }

  addParty(): void {
    const party: OpposingParty = { name: '', phone: '', role: '', email: '', address: '' };
    this.answers.opposingParties.push(party);
  }

  removeParty(index: number): void {
    this.answers.opposingParties.splice(index, 1);
  }

  private static readonly EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private static readonly PHONE_PATTERN = /^\d{10}$/;

  /** Q3 phone: exactly 10 numeric digits, no formatting characters
   *  (country code, spaces, dashes, etc.) — this is also the final
   *  validation layer for values that reach `party.phone` without going
   *  through setPartyPhone (paste/autofill/hydrated draft data). */
  isPartyPhoneValid(party: OpposingParty): boolean {
    return LegalInformationQuestionnaireComponent.PHONE_PATTERN.test((party.phone || '').trim());
  }

  /** Q3 email is optional — an empty value is valid; a non-empty value
   *  must match a standard "local@domain.tld" shape. */
  isPartyEmailValid(party: OpposingParty): boolean {
    const email = (party.email || '').trim();
    if (!email) { return true; }
    return LegalInformationQuestionnaireComponent.EMAIL_PATTERN.test(email);
  }

  /** Live input handler for Q3 Phone — strips non-digit characters (no
   *  country code/spaces/dashes) and blocks entry past 10 digits, the
   *  same way setTextValue/truncateToWordLimit cap other fields as the
   *  user types, rather than only flagging it after the fact. Paste and
   *  autofill also flow through this handler (ngModelChange fires on any
   *  value change), so both are capped here too — isPartyPhoneValid above
   *  remains the authoritative check for anything that reaches
   *  `party.phone` some other way (e.g. hydrated draft data). */

  setPartyPhone(party: OpposingParty, value: string): void {
  const digits = (value || '').replace(/\D/g, '');
  party.phone = digits.slice(0, 10);
}

allowOnlyDigits(event: KeyboardEvent): void {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End'
  ];

  if (allowedKeys.includes(event.key)) {
    return;
  }

  // Allow only 0-9
  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
  }
}
  // ---------- Q5: events ----------

  addEvent(): void {
    if (this.answers.events.length >= 50) { return; }
    const event: CaseEvent = { date: null, event: '', location: '', time: null };
    this.answers.events.push(event);
  }

  removeEvent(index: number): void {
    this.answers.events.splice(index, 1);
    // Avoid leaving the Time picker panel floating/mismatched against a row
    // that no longer exists (or now refers to a different event).
    if (this.openTimeDropdownIndex !== null) {
      this.closeEventTimeDropdown();
    }
  }

  /** Q5 event text: 0/50 word counter validity, same word-count utility and
   *  50-word convention already used for the "Other" free-text fields
   *  (see isDocOtherValid / isOtherTextValid) — display-only, does not
   *  change existing step validation/save behavior. */
  isEventTextValid(evt: CaseEvent): boolean {
    return this.wordCount(evt.event || '') <= 50;
  }

  /** Q5 required-field validity for a single event row: Date and Event are
   *  required, Time is always optional. Used by isStepValid() to gate
   *  Save & Next / Review so an incomplete row can't be saved (previously
   *  the 'events' step had no case in isStepValid() and always fell
   *  through to `default: return true`, so this step was never checked). */
  isEventValid(evt: CaseEvent): boolean {
    if (!evt.date) { return false; }
    if (!(evt.event || '').trim()) { return false; }
    return this.isEventTextValid(evt);
  }

  // ---------- Q5: Time picker (modernized 3-column dropdown, replacing the
  // native <input type="time">) — reuses the same fixed-position
  // .li-ms/.li-ms-panel scaffold (msTriggerEl/msPanelStyle/msOpenUpward/
  // updateDropdownPosition) as the multi-select and Document Type combos
  // above, and closes those two when it opens (and vice versa, see
  // toggleDropdown/toggleDocTypeDropdown) since they all share that one
  // positioned panel. `evt.time` keeps the exact same "HH:mm" 24-hour
  // string the native input produced, so buildAnswerPayloadForStep /
  // hydratePreReadAnswers in pre-read.model.ts need no changes at all —
  // these helpers only convert that string to/from the picker's 12-hour
  // display. ----------

  readonly eventTimeHours: number[] = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
  readonly eventTimeMinutes: number[] = Array.from({ length: 60 }, (_, i) => i);   // 00–59
  readonly eventTimePeriods: Array<'AM' | 'PM'> = ['AM', 'PM'];

  openTimeDropdownIndex: number | null = null;

  toggleEventTimeDropdown(index: number, event?: MouseEvent): void {
    if (this.openTimeDropdownIndex === index) {
      this.closeEventTimeDropdown();
      return;
    }
    this.closeDropdown();
    this.closeDocTypeDropdown();
    this.openTimeDropdownIndex = index;
    this.msTriggerEl = (event?.currentTarget as HTMLElement) || null;
    this.updateDropdownPosition();
    document.addEventListener('scroll', this.msReposition, true);
    window.addEventListener('resize', this.msReposition);
  }

  closeEventTimeDropdown(): void {
    this.openTimeDropdownIndex = null;
    this.msTriggerEl = null;
    document.removeEventListener('scroll', this.msReposition, true);
    window.removeEventListener('resize', this.msReposition);
  }

  isEventTimeDropdownOpen(index: number): boolean {
    return this.openTimeDropdownIndex === index;
  }

  hour12FromEventTime(value: string | null | undefined): number | null {
    if (!value) { return null; }
    const h = parseInt(value.split(':')[0], 10);
    if (isNaN(h)) { return null; }
    const h12 = h % 12;
    return h12 === 0 ? 12 : h12;
  }

  minuteFromEventTime(value: string | null | undefined): number | null {
    if (!value) { return null; }
    const m = parseInt(value.split(':')[1], 10);
    return isNaN(m) ? null : m;
  }

  periodFromEventTime(value: string | null | undefined): 'AM' | 'PM' {
    if (!value) { return 'AM'; }
    const h = parseInt(value.split(':')[0], 10);
    return h >= 12 ? 'PM' : 'AM';
  }

  formatEventTimeDisplay(value: string | null | undefined): string {
    const h12 = this.hour12FromEventTime(value);
    const m = this.minuteFromEventTime(value);
    if (h12 === null || m === null) { return ''; }
    return `${this.padTwo(h12)}:${this.padTwo(m)} ${this.periodFromEventTime(value)}`;
  }

  padTwo(n: number): string {
    return String(n).padStart(2, '0');
  }

  private buildEventTimeString(hour12: number, minute: number, period: 'AM' | 'PM'): string {
    let h24 = hour12 % 12;
    if (period === 'PM') { h24 += 12; }
    return `${this.padTwo(h24)}:${this.padTwo(minute)}`;
  }

  /** Picking any one of Hour/Minute/AM-PM applies immediately (wheel-picker
   *  convention) — unset parts default to 12 / 00 / AM the first time a
   *  value is picked, so the field always holds a complete, valid time. */
  selectEventHour(evt: CaseEvent, hour: number): void {
    const minute = this.minuteFromEventTime(evt.time) ?? 0;
    const period = this.periodFromEventTime(evt.time);
    evt.time = this.buildEventTimeString(hour, minute, period);
  }

  selectEventMinute(evt: CaseEvent, minute: number): void {
    const hour = this.hour12FromEventTime(evt.time) ?? 12;
    const period = this.periodFromEventTime(evt.time);
    evt.time = this.buildEventTimeString(hour, minute, period);
  }

  selectEventPeriod(evt: CaseEvent, period: 'AM' | 'PM'): void {
    const hour = this.hour12FromEventTime(evt.time) ?? 12;
    const minute = this.minuteFromEventTime(evt.time) ?? 0;
    evt.time = this.buildEventTimeString(hour, minute, period);
  }

  clearEventTime(evt: CaseEvent): void {
    evt.time = null;
  }

  // ---------- Q6: documents (PRD Part 8/9/11-19) ----------

 
  isDocOtherValid(doc: SupportingDocument): boolean {
    if (doc.documentType !== 'Other') { return true; }
    const text = (doc.otherCategoryText || '').trim();
    if (!text) { return false; }
    return this.wordCount(text) <= 50;
  }

  /** Q6 document name: 0/50 word counter validity, same display-only
   *  convention as isEventTextValid — does not change existing step
   *  validation/save behavior. */
  isDocNameValid(doc: SupportingDocument): boolean {
    return this.wordCount(doc.documentName || '') <= 50;
  }

  documentCategoryOptions(): string[] {
    return this.currentQuestion.options || [];
  }

  
  onFilesSelected(fileList: FileList | null): void {
    if (!fileList || !fileList.length) { return; }
    if (this.answers.documents.length + fileList.length > 50) {
      this.toastr.warning('You can upload a maximum of 50 documents.');
      return;
    }
    const defaultType = this.documentCategoryOptions()[0] || 'Other';
    const added: SupportingDocument[] = [];
    Array.from(fileList).forEach(file => {
      const doc: SupportingDocument = {
        documentType: defaultType,
        documentName: file.name,
        file,
        uploaded: false
      };
      this.answers.documents.push(doc);
      added.push(doc);
    });
    //added.forEach(doc => this.uploadDocumentAndTrack(doc).subscribe());
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.onFilesSelected(event.dataTransfer?.files ?? null);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  removeDocument(index: number): void {
    this.answers.documents.splice(index, 1);
  }


  private persistDocumentsStep(onSuccess: () => void): void {
    if (this.answers.documents.some(d => d.uploading)) {

      this.toastr.warning('Please wait for all documents to finish uploading.');
      return;
    }
    if (this.saving) { onSuccess(); return; }
    this.ensurePreReadId().subscribe({
      next: () => this.persistDocumentsStepWithId(onSuccess),
      error: () => this.toastr.error('Could not start your Legal Information entry. Please try again.')
    });
  }


  private persistDocumentsStepWithId(onSuccess: () => void): void {
    if (!this.answers.hasDocuments) {
      // PRD Part 17: no-document case — patch has_documents:false, no upload attempted.
      this.patchSupportingDocuments([], onSuccess);
      return;
    }

    if (this.answers.documents.some(d => d.uploading)) {
      this.toastr.warning('Please wait for all documents to finish uploading.');
      return;
    }

    const failed = this.answers.documents.filter(d => d.file && !d.uploaded && d.uploadError);
    if (failed.length) {
      this.toastr.error('Please retry or remove the document(s) that failed to upload before continuing.');
      return;
    }

    // Push any in-progress document renames to the document store first
    // (same edit API used by add-document-dialog.component), then continue
    // with the existing upload-then-patch flow unchanged.
    this.renamePendingDocuments().subscribe({
      next: (allRenamesOk) => {
        if (!allRenamesOk) { return; } // failure already toasted per-document
        this.continueDocumentsStepAfterRename(onSuccess);
      },
      error: () => this.toastr.error('Could not update document name(s). Please try again.')
    });
  }

  /** Finds documents whose name was edited since it was last confirmed with
   *  the server and renames them via the same document-update API used by
   *  add-document-dialog.component (AuthService.update_personal). Documents
   *  that were never uploaded (no id yet) are skipped — their current name
   *  is simply what gets uploaded. */
  // private renamePendingDocuments(): Observable<boolean> {
  //   const renamed = this.answers.documents.filter(d =>
  //     d.uploaded && !!d.id &&
  //     !!(d.documentName || '').trim() &&
  //     (d.documentName || '').trim() !== (d.originalDocumentName || '').trim()
  //   );
  //   if (!renamed.length) { return of(true); }

  //   return forkJoin(renamed.map(doc => this.renameDocument(doc))).pipe(
  //     map(results => results.every(ok => ok))
  //   );
  // }
  private renamePendingDocuments(): Observable<boolean> {
  const changed = this.answers.documents.filter(d => {
    if (!d.uploaded || !d.id) { return false; }
    if (!(d.documentName || '').trim()) { return false; }

    const nameChanged =
      (d.documentName || '').trim() !== (d.originalDocumentName || '').trim();
    const typeChanged =
      d.originalDocumentType !== undefined &&
      d.documentType !== d.originalDocumentType;
    const otherChanged =
      d.documentType === 'Other' &&
      (d.otherCategoryText || '').trim() !== (d.originalOtherCategoryText || '').trim();

    return nameChanged || typeChanged || otherChanged;
  });

  if (!changed.length) { return of(true); }
  return forkJoin(changed.map(doc => this.renameDocument(doc))).pipe(
    map(results => results.every(ok => ok))
  );
}

  private renameDocument(doc: SupportingDocument): Observable<boolean> {
    return this.resolveLegalFinanceCategoryId().pipe(
      switchMap(categoryId => {
        if (!categoryId || !this.isValidObjectId(categoryId)) {
          const msg = 'Could not rename this document (category could not be resolved).';
          doc.uploadError = msg;
          this.toastr.error(msg);
          return of(false);
        }
        // Same category/user-tag derivation used at upload time (see
        // uploadDocumentAndTrack) so the rename call doesn't blank out or
        // change the document's existing categorisation.
        const category = doc.documentType || 'Other';
        const userTag = category === 'Other'
          ? (doc.otherCategoryText || '').trim().toLowerCase()
          : (DOCUMENT_USER_TAG_MAP[category] || category.toLowerCase());
        const name = (doc.documentName || '').trim();
        const payload = {
          name,
          description: name,
          expiration_date: '',
          tags: [categoryId],
          user_tags: [userTag]
        };
        return this.auth.update_personal(doc.id as string, payload).pipe(
          map(() => {
            doc.originalDocumentName = doc.documentName;
            doc.originalDocumentType = doc.documentType;
            doc.originalOtherCategoryText = doc.otherCategoryText;
            return true;
          }),
          catchError((err: any) => {
            const reason = err?.error?.msg || err?.message || 'Could not rename this document.';
            doc.uploadError = reason;
            this.toastr.error(`Could not rename "${name}": ${reason}`);
            return of(false);
          })
        );
      })
    );
  }

  private continueDocumentsStepAfterRename(onSuccess: () => void): void {
    const alreadyUploadedIds = this.answers.documents
      .filter(d => d.uploaded && d.id)
      .map(d => d.id as string);


    const neverAttempted = this.answers.documents.filter(d => d.file && !d.uploaded && !d.uploadError);

    if (!neverAttempted.length) {
  
      this.patchSupportingDocuments(alreadyUploadedIds, onSuccess);
      return;
    }

    forkJoin(neverAttempted.map(doc => this.uploadDocumentAndTrack(doc))).subscribe({
      next: (uploadedIds) => {
        // PRD Part 18: don't silently include failed uploads.
        const failedCount = uploadedIds.filter(id => !id).length;
        if (failedCount > 0) {
          const firstReason = neverAttempted.find(d => d.uploadError)?.uploadError;
          this.toastr.error(
            firstReason
              ? `${failedCount} document(s) failed to upload: ${firstReason}`
              : `${failedCount} document(s) failed to upload. Please retry or remove them before continuing.`
          );
          return; // do not advance / do not PATCH with a partial, silently-accepted set
        }
        const allIds = [...alreadyUploadedIds, ...(uploadedIds.filter((id): id is string => !!id))];
        this.patchSupportingDocuments(allIds, onSuccess);
      },
      error: () => this.toastr.error('Could not upload your documents.')
    });
  }

  retryDocumentUpload(index: number): void {
    const doc = this.answers.documents[index];
    if (!doc || !doc.file || doc.uploaded || doc.uploading) { return; }
    this.uploadDocumentAndTrack(doc).subscribe({
      next: (id) => {
        if (id) { this.toastr.success('Document uploaded.'); }
     
      },
      error: () => this.toastr.error('Could not upload this document. Please try again.')
    });
  }


  // private buildSupportingDocumentsAnswer(documentIds: string[]): ReturnType<typeof buildAnswerPayloadForStep>[number] {
  //   const categories = this.answers.hasDocuments
  //     ? Array.from(new Set(this.answers.documents
  //         .filter(d => d.documentType && d.documentType !== 'Other')
  //         .map(d => SUPPORTING_DOC_CATEGORY_MAP[d.documentType] || d.documentType)))
  //     : [];
  //   const otherEntry = this.answers.documents.find(d => d.documentType === 'Other');
  //   return {
  //     question_code: QUESTION_CODES[DOCUMENTS_UI_STEP],
  //     value: {
  //       has_documents: !!this.answers.hasDocuments,
  //       categories,
  //       other: otherEntry ? (otherEntry.otherCategoryText || '').trim() : ''
  //     },
  //     documents: documentIds
  //   };
  // }

  private buildSupportingDocumentsAnswer(documentIds: string[]): ReturnType<typeof buildAnswerPayloadForStep>[number] {
    const uploadedDocs = this.answers.documents.filter(d => d.uploaded && d.id);

    const categories = this.answers.hasDocuments
      ? Array.from(new Set(uploadedDocs
        .filter(d => d.documentType && d.documentType !== 'Other')
        .map(d => SUPPORTING_DOC_CATEGORY_MAP[d.documentType] || d.documentType)))
      : [];

    // NEW — the actual fix. This is the only place in the payload that ties
    // a document's own id to its own type, instead of collapsing every
    // document's type into one shared, deduplicated set.
    const documentCategories = uploadedDocs.map(d => ({
      id: d.id,
      category: d.documentType === 'Other' ? 'other' : (SUPPORTING_DOC_CATEGORY_MAP[d.documentType] || d.documentType),
      other_text: d.documentType === 'Other' ? (d.otherCategoryText || '').trim() : ''
    }));

    const otherEntry = this.answers.documents.find(d => d.documentType === 'Other');

    return {
      question_code: QUESTION_CODES[DOCUMENTS_UI_STEP],
      value: {
        has_documents: !!this.answers.hasDocuments,
        categories,                 // kept, in case anything else still reads it
        other: otherEntry ? (otherEntry.otherCategoryText || '').trim() : '',
        document_categories: documentCategories   // NEW
      },
      documents: documentIds        // unchanged — still needed as-is
    };
  }

  private patchSupportingDocuments(documentIds: string[], onSuccess: () => void): void {
    if (!this.preReadId) { onSuccess(); return; }

    this.saving = true;
    this.preReadService.patchAnswers(this.preReadId, toBackendCurrentStep(DOCUMENTS_UI_STEP), [
      this.buildSupportingDocumentsAnswer(documentIds)
    ]).pipe(
      finalize(() => this.saving = false)
    ).subscribe({
      // Reset `saving` here (not just via `finalize`) before invoking the
      // continuation: `finalize`'s teardown only runs on the observable's
      // complete/unsubscribe, which happens *after* this `next` handler
      // returns. Save for Later chains persistAllAnswered() as onSuccess,
      // and persistAllAnswered() itself bails out early if `this.saving`
      // is still true — so without this, the aggregated per-step patch
      // (which carries the current step's just-chosen answer) was being
      // silently skipped whenever Save for Later ran from step 6 onward.
      next: () => {
        this.saving = false;
        onSuccess();
      },
      error: () => this.toastr.error('Could not save your supporting documents.')
    });
  }


  private isValidObjectId(id: string): boolean {
    return /^[0-9a-fA-F]{24}$/.test(id);
  }

  private uploadDocumentAndTrack(doc: SupportingDocument): Observable<string | null> {
    const file = doc.file as File;
    doc.uploading = true;
    doc.uploadError = undefined;
    this.activeUploadCount++;
    this.uploading = true;

    return this.resolveLegalFinanceCategoryId().pipe(
      switchMap(categoryId => {
        if (!categoryId) {
          // PRD Part 10: never proceed with an invented/hardcoded category id.
          const msg = 'Could not determine the document category for this upload. Please close and reopen this Legal Information entry.';
          doc.uploadError = msg;
          return of(null);
        }
        if (!this.isValidObjectId(categoryId)) {
      
          const msg = 'The Legal & Finance document category could not be resolved correctly (invalid category id). Please close and reopen this Legal Information entry, or contact support if this persists.';
          doc.uploadError = msg;
          return of(null);
        }

        const category = doc.documentType || 'Other';
      
        const userTag = category === 'Other'
          ? (doc.otherCategoryText || '').trim().toLowerCase()
          : (DOCUMENT_USER_TAG_MAP[category] || category.toLowerCase());

        // Use the current (possibly user-edited) Document Name rather than
        // the raw file.name, so a name change made after a duplicate-name
        // rejection is actually reflected on the retry — otherwise the
        // retry would silently resend the original conflicting name.
        const uploadName = (doc.documentName || '').trim() || file.name;
        const formData = new FormData();
        formData.append('name', uploadName);
        formData.append('description', uploadName);
        formData.append('expiration_date', '');
        formData.append('tags', JSON.stringify([categoryId]));
        formData.append('user_tags', JSON.stringify([userTag]));
        formData.append('file', file);

        return this.auth.add_personal_doc(PERSONAL_DOC_INDEX, formData).pipe(
          map((res: any) => {
            if (res?.error === false) {
            
            
              const id = res?.docid || res?.data?.id || res?.data?._id || res?.data?.documentId || res?.data?.document_id || null;
              if (!id) {
                const reason = 'Upload succeeded but no document ID was returned. Please retry.';
                console.error('[Legal Information] Upload succeeded but response had no recognizable document id:', file.name, res);
                doc.uploadError = reason;
              } else {
                // Backend auto-renames on duplicate filenames (e.g. "poster2.png"
                // -> "poster2 (4).png"). Reflect the actual stored name returned
                // by the API instead of leaving the pre-upload name in the UI.
                const backendDocName = res?.doc_name || res?.data?.doc_name || res?.data?.name;
                if (backendDocName) {
                  doc.documentName = backendDocName;
                }
              }
              return id;
            }
         
            const reason = res?.msg || 'Upload was rejected by the server.';
            console.error('[Legal Information] Document upload rejected:', file.name, res);
            doc.uploadError = reason;
            return null;
          }),
          catchError((err: any) => {
            const reason = err?.error?.msg || err?.message || 'Upload failed. Please try again.';
            console.error('[Legal Information] Document upload error:', file.name, err);
            doc.uploadError = reason;
            return of(null);
          })
        );
      }),
      map(id => {
        if (id) {
          doc.uploaded = true;
          doc.id = id;
          doc.originalDocumentName = doc.documentName;
          doc.originalDocumentType = doc.documentType;
          doc.originalOtherCategoryText = doc.otherCategoryText;
        }
        return id;
      }),
      finalize(() => {
        doc.uploading = false;
        this.activeUploadCount = Math.max(0, this.activeUploadCount - 1);
        this.uploading = this.activeUploadCount > 0;
      })
    );
  }

  // ---------- Q9: deadlines ----------

  readonly deadlineTypes = ['Filing deadline', 'Hearing date', 'Limitation deadline', 'Notice reply deadline', 'Other'];

  isDeadlineTypeSelected(type: string): boolean {
    return this.answers.deadlines.some(d => d.type === (type as DeadlineEntry['type']));
  }

  toggleDeadlineType(type: string): void {
    const existingIndex = this.answers.deadlines.findIndex(d => d.type === (type as DeadlineEntry['type']));
    if (existingIndex > -1) {
      this.answers.deadlines.splice(existingIndex, 1);
    } else {
      this.answers.deadlines.push({ type: type as DeadlineEntry['type'], date: null, notes: '' });
    }
  }

  deadlineFor(type: string): DeadlineEntry | undefined {
    return this.answers.deadlines.find(d => d.type === (type as DeadlineEntry['type']));
  }


  // ---------------------------------------------------------------------------
  // Voice input — logical voice session layer over the Web Speech API.
  //
  // The browser ends a recognition instance on its own (after a pause, or a
  // short hard timeout) even when the user is still dictating. This layer
  // keeps ONE logical session alive across however many recognition
  // instances the browser decides to create, so the user experiences a
  // single continuous dictation until they tap the mic again.
  // ---------------------------------------------------------------------------

  private recognition: any = null;
  listeningFor: string | null = null;

  voiceStatus: 'idle' | 'listening' | 'error' = 'idle';
  voiceErrorFor: string | null = null;
  voiceErrorMessage = '';
  private voiceErrorTimeout: any = null;

  /** Incremented on every explicit start/stop. Every async recognition
   *  callback checks it, so a callback from a superseded instance can never
   *  mutate the current session's state. */
  private voiceSessionId = 0;
  /** Pending restart timer for the current logical session (see handleVoiceEnd). */
  private voiceRestartTimer: any = null;

  /** Text committed by recognition instances that have already ended in this
   *  logical session. Instance-local text is kept separately so a restart
   *  never re-appends what was already committed. */
  private voiceCommitted = '';
  /** Final text from the CURRENTLY running recognition instance. */
  private voiceInstanceFinal = '';
  /** Interim (not yet finalized) text from the currently running instance. */
  private voiceInterim = '';
  /** Field value that existed before the logical session started (append mode). */
  private voiceBaseValue = '';
  /** Guards against the browser's no-speech error ending a session the user
   *  never actually spoke into, vs. a session that has produced real text. */
  private voiceHeardSomething = false;

  private readonly VOICE_REPLACE_FIELDS = new Set<string>();

  private voiceModeFor(targetKey: string): 'replace' | 'append' {
    return this.VOICE_REPLACE_FIELDS.has(targetKey) ? 'replace' : 'append';
  }

  isListening(targetKey: string): boolean {
    return this.listeningFor === targetKey && this.voiceStatus === 'listening';
  }

  hasVoiceError(targetKey: string): boolean {
    return this.voiceErrorFor === targetKey && this.voiceStatus === 'error';
  }

  private showVoiceError(targetKey: string, message: string): void {
    this.voiceStatus = 'error';
    this.voiceErrorFor = targetKey;
    this.voiceErrorMessage = message;
    this.listeningFor = null;

    if (this.voiceErrorTimeout) { clearTimeout(this.voiceErrorTimeout); }
    this.voiceErrorTimeout = setTimeout(() => {
      if (this.voiceErrorFor === targetKey) {
        this.runInZone(() => {
          this.voiceStatus = 'idle';
          this.voiceErrorFor = null;
        });
      }
    }, 4000);
  }

  private runInZone(fn: () => void): void {
    if (this.ngZone) {
      this.ngZone.run(fn);
    } else {
      fn();
    }
  }

  /** Speech engines are inconsistent about whitespace immediately around the
   *  punctuation they return (some emit "matter ." or double spaces when
   *  interim/final pieces are stitched together). This ONLY tidies
   *  whitespace — it never removes, replaces, or infers a punctuation
   *  character, so whatever punctuation the engine supplied survives
   *  exactly as given. */
  private normalizeVoiceTranscript(text: string): string {
    if (!text) { return text; }
    return text
      .replace(/\s+([,.;:!?])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  }

  private joinVoiceParts(...parts: string[]): string {
    return parts.map(p => (p || '').trim()).filter(Boolean).join(' ');
  }

  /**
   * Sentence-case for dictated text. Deterministic and meaning-safe: it only
   * ever UPPERCASES the first letter of the text and the first letter after a
   * sentence-ending '.', '!' or '?'. It never lowercases anything, so proper
   * nouns and acronyms the engine already capitalised ("ABC Builders", "FIR")
   * are untouched, and it never adds, removes or guesses punctuation.
   *
   * `capitaliseStart` is false when dictation is continuing a sentence the
   * user already typed (append mode onto text that doesn't end a sentence),
   * so mid-sentence dictation isn't wrongly capitalised.
   */
  private sentenceCaseVoiceText(text: string, capitaliseStart: boolean): string {
    if (!text) { return text; }

    let result = text;

    if (capitaliseStart) {
      // Capitalise the first letter, skipping any leading quote/bracket.
      result = result.replace(/^([^\p{L}]*)(\p{L})/u, (_m, lead, ch) => lead + ch.toUpperCase());
    }

    // Capitalise the first letter after a sentence-ending mark. Requires the
    // whitespace to be present, so decimals ("5.30") and abbreviations
    // ("p.m.") are left alone.
    result = result.replace(
      /([.!?])(\s+)([^\p{L}]*)(\p{L})/gu,
      (_m, mark, space, lead, ch) => mark + space + lead + ch.toUpperCase()
    );

    return result;
  }

  /** Builds the field value for the current logical session state and pushes
   *  it through the EXISTING setters, so word limits, validation, form state
   *  and change detection all behave exactly as they do for typing. */
  private applyVoiceValue(targetKey: string): void {
    const rawJoined = this.joinVoiceParts(this.voiceCommitted, this.voiceInstanceFinal, this.voiceInterim);
    // Convert explicit SPOKEN punctuation commands ("comma", "full stop", …)
    // into marks before whitespace normalization, so normalizeVoiceTranscript
    // can tidy up any spacing the substitution leaves around them. This is
    // ONLY the literal command words a user speaks on purpose — see
    // voice-punctuation.util.ts for exactly what is and isn't converted.
    const withSpokenPunctuation = applySpokenPunctuationCommands(rawJoined);
    const normalised = this.normalizeVoiceTranscript(withSpokenPunctuation);

    // Start a new sentence unless we're continuing text the user already has
    // in the field that doesn't end a sentence.
    const base = this.voiceBaseValue;
    const capitaliseStart = !base || /[.!?]["')\]]?$/.test(base.trim());

    const dictated = this.sentenceCaseVoiceText(normalised, capitaliseStart);

    const value = dictated
      ? this.joinVoiceParts(base, dictated)
      : base;

    if (targetKey === 'caseTitle') {
      this.setCaseTitle(value);
    } else {
      this.setTextValue(targetKey, value);
    }
  }

  /**
   * Mic button handler. Starts a logical session if idle, and stops it
   * immediately (single click, no stale state) if this field is listening.
   */
  toggleVoiceInput(targetKey: string): void {
    const SpeechRecognitionCtor = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SpeechRecognitionCtor) {
      this.showVoiceError(targetKey, 'Voice input is not supported in this browser. Try Chrome or Edge, or type instead.');
      return;
    }

    // Toggle off — this is the ONLY place a user-initiated stop happens.
    if (this.isListening(targetKey)) {
      this.stopVoiceRecognition();
      return;
    }

    // Any other state (idle, error, or a session on a different field, or a
    // session whose recognition instance the browser already killed) starts
    // fresh. stopVoiceRecognition() invalidates whatever came before, so a
    // dead-but-still-flagged session can never swallow this click.
    this.stopVoiceRecognition();

    if (this.voiceErrorTimeout) { clearTimeout(this.voiceErrorTimeout); }
    this.voiceErrorFor = null;
    this.voiceStatus = 'listening';
    this.listeningFor = targetKey;

    const mode = this.voiceModeFor(targetKey);
    this.voiceBaseValue = mode === 'append' ? (this.textValue(targetKey) || '').trim() : '';
    this.voiceCommitted = '';
    this.voiceInstanceFinal = '';
    this.voiceInterim = '';
    this.voiceHeardSomething = false;

    const sessionId = ++this.voiceSessionId;
    this.startVoiceRecognition(targetKey, sessionId, SpeechRecognitionCtor);
  }

  /** Creates and starts ONE recognition instance for the given logical session. */
  private startVoiceRecognition(targetKey: string, sessionId: number, Ctor: any): void {
    if (sessionId !== this.voiceSessionId) { return; }

    let recognition: any;
    try {
      recognition = new Ctor();
    } catch {
      this.showVoiceError(targetKey, 'Could not start voice input. Please try again.');
      return;
    }

    recognition.lang = 'en-IN';
    recognition.interimResults = true;
    // Ask the browser to keep the instance open across short pauses where it
    // supports doing so. The restart logic below still covers the browsers
    // (and situations) where the instance ends anyway.
    recognition.continuous = true;
    // Single best hypothesis only — we render exactly one transcript, so
    // there is nothing useful to do with alternate candidates, and asking
    // for none keeps every engine's response shape as simple as possible.
    recognition.maxAlternatives = 1;
    // Chrome 151+ exposes a native automatic-punctuation model behind this
    // flag, on the standard network recognition path. Plain property set on
    // an `any` object: browsers/versions without it simply ignore it — no
    // error, no behavior change.
    //
    // Deliberately NOT opting into on-device recognition (processLocally):
    // as of testing, Chrome's on-device engine does not implement automatic
    // punctuation at all, and its capability-check API has open, documented
    // reliability issues (available() reporting "available" for a language
    // that then fails to start). This app has no offline requirement, so
    // there's no upside to it here — only the risk of both problems. If a
    // future need for offline dictation arises, on-device support can be
    // reintroduced as an explicit, separately-tested opt-in.
    recognition.unspokenPunctuation = true;

    recognition.onresult = (event: any) => this.handleVoiceResult(event, targetKey, sessionId);
    recognition.onerror = (event: any) => this.handleVoiceError(event, targetKey, sessionId);
    recognition.onend = () => this.handleVoiceEnd(targetKey, sessionId, Ctor);

    this.recognition = recognition;

    try {
      recognition.start();
    } catch {
      // start() throws if this instance is already starting/running. That's a
      // harmless race, not something to surface to the user — the instance is
      // live either way, and onend/onerror still drive the session from here.
    }
  }

  private handleVoiceResult(event: any, targetKey: string, sessionId: number): void {
    this.runInZone(() => {
      if (sessionId !== this.voiceSessionId) { return; }

      // Rebuild this instance's transcript from scratch on every event rather
      // than appending deltas. event.results holds every result for THIS
      // instance, so a full rebuild is idempotent and cannot double-append a
      // piece that a previous event already contributed.
      let instanceFinal = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const piece = result[0]?.transcript || '';
        if (!piece) { continue; }
        if (result.isFinal) {
          instanceFinal = instanceFinal ? `${instanceFinal} ${piece}` : piece;
        } else {
          interim = interim ? `${interim} ${piece}` : piece;
        }
      }

      this.voiceInstanceFinal = instanceFinal;
      this.voiceInterim = interim;
      if (instanceFinal.trim() || interim.trim()) { this.voiceHeardSomething = true; }

      // Live update — the field reflects speech as it is recognized, without
      // waiting for the instance to end.
      this.applyVoiceValue(targetKey);
    });
  }

  private handleVoiceError(event: any, targetKey: string, sessionId: number): void {
    this.runInZone(() => {
      if (sessionId !== this.voiceSessionId) { return; }

      const code = event?.error;

      // 'aborted' is what a normal stop()/restart produces — never an error
      // worth showing. 'no-speech' during an ongoing session is also normal
      // (the user paused); let onend restart instead of failing the session.
      if (code === 'aborted') { return; }
      if (code === 'no-speech' && this.voiceHeardSomething) { return; }

      const message =
        code === 'not-allowed' || code === 'service-not-allowed'
          ? 'Microphone access was denied. Allow microphone permission to use voice input.'
          : code === 'no-speech'
            ? "Didn't catch that — please try again."
            : code === 'audio-capture'
              ? 'No microphone was found on this device.'
              : code === 'network'
                ? 'Voice input needs a network connection. Please check your connection and try again.'
                : code === 'language-not-supported'
                  ? "Voice input doesn't support this language on this device. Please continue typing."
                  : 'Voice input ran into a problem. Please try again or type instead.';

      // Terminal error: end the logical session so onend cannot restart it.
      this.voiceSessionId++;
      this.clearVoiceRestartTimer();
      this.detachRecognition();
      this.showVoiceError(targetKey, message);
    });
  }

  /**
   * The browser ended a recognition instance. If the logical session is still
   * active (the user has NOT tapped stop), commit this instance's final text
   * and start a fresh instance so dictation continues seamlessly.
   */
  private handleVoiceEnd(targetKey: string, sessionId: number, Ctor: any): void {
    this.runInZone(() => {
      // A superseded session (user stopped, switched fields, or a terminal
      // error fired) bumped voiceSessionId — so this end is final, not a restart.
      if (sessionId !== this.voiceSessionId) { return; }
      if (this.voiceStatus !== 'listening' || this.listeningFor !== targetKey) { return; }

      // Commit what this instance finalized, then clear instance-local state
      // so the next instance starts from empty and contributes only new speech.
      const committedSoFar = this.joinVoiceParts(this.voiceCommitted, this.voiceInstanceFinal);
      this.voiceCommitted = committedSoFar;
      this.voiceInstanceFinal = '';
      this.voiceInterim = '';

      this.detachRecognition();

      // Controlled, single-shot restart — never a loop, never recursive.
      this.clearVoiceRestartTimer();
      this.voiceRestartTimer = setTimeout(() => {
        this.voiceRestartTimer = null;
        this.runInZone(() => {
          if (sessionId !== this.voiceSessionId) { return; }
          if (this.voiceStatus !== 'listening' || this.listeningFor !== targetKey) { return; }
          this.startVoiceRecognition(targetKey, sessionId, Ctor);
        });
      }, 120);
    });
  }

  /** Detaches handlers from the live instance and drops the reference, so a
   *  late callback from it can never reach the component. */
  private detachRecognition(): void {
    const recognition = this.recognition;
    this.recognition = null;
    if (!recognition) { return; }
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.stop();
    } catch {
      // Already stopped/inactive — nothing further to do.
    }
  }

  private clearVoiceRestartTimer(): void {
    if (this.voiceRestartTimer) {
      clearTimeout(this.voiceRestartTimer);
      this.voiceRestartTimer = null;
    }
  }

  /**
   * Explicit stop. The session is invalidated FIRST, so the onend that
   * stop() triggers sees a stale sessionId and cannot restart. UI state is
   * cleared synchronously — one click, no stale listening state.
   */
  private stopVoiceRecognition(): void {
    this.voiceSessionId++;
    this.clearVoiceRestartTimer();

    // Keep whatever the instance had already produced — this is what the user
    // can see in the field at the moment they tap stop.
    this.voiceCommitted = this.joinVoiceParts(this.voiceCommitted, this.voiceInstanceFinal, this.voiceInterim);
    this.voiceInstanceFinal = '';
    this.voiceInterim = '';

    this.detachRecognition();

    if (this.voiceStatus === 'listening') {
      this.voiceStatus = 'idle';
      this.listeningFor = null;
    }
  }

  ngOnDestroy(): void {
 
    document.removeEventListener('scroll', this.msReposition, true);
    window.removeEventListener('resize', this.msReposition);
    if (this.voiceErrorTimeout) { clearTimeout(this.voiceErrorTimeout); }
    if (this.wordLimitNoticeTimeout) { clearTimeout(this.wordLimitNoticeTimeout); }
    // stopVoiceRecognition() invalidates the logical session, clears the
    // pending restart timer and detaches every recognition handler, so no
    // speech callback can reach the destroyed component.
    this.stopVoiceRecognition();
  }

  // getDocuments() {
  //   this.auth.get_tags('citizen_primary').subscribe({
  //     next: (tagRes: any) => {
  //       const lfID = tagRes.data.categories.find(
  //         (c: any) => c.tagname === 'LegalFinance'
  //       )?.id;

  //       if (lfID) {
  //         this.auth.get_personal_docs_tag(lfID, 'citizen_primary').subscribe({
  //           next: (res: any) => {
  //             this.iDocuments = res.data?.documents || [];
  //           }
  //         });
  //       }
  //     }
  //   });
  // }
  getDocuments() {
  this.auth.get_tags('citizen_primary').subscribe({
    next: (tagRes: any) => {
      const lfID = tagRes.data.categories.find(
        (c: any) => c.tagname === 'LegalFinance'
      )?.id;

      if (lfID) {
        this.auth.get_personal_docs_tag(lfID, 'citizen_primary').subscribe({
          next: (res: any) => {
            this.iDocuments = res.data?.documents || [];
            this.docLibraryLoading = false;
          },
          error: () => {
            this.docLibraryLoading = false;
            this.toastr.error('Could not load your document library.');
          }
        });
      } else {
        this.docLibraryLoading = false;
      }
    },
    error: () => {
      this.docLibraryLoading = false;
      this.toastr.error('Could not load your document library.');
    }
  });
}

  /** Q6: id/name extraction is defensive because the exact response shape
 *  from get_personal_docs_tag isn't confirmed yet — mirrors the same
 *  multi-key fallback pattern already used for the upload response in
 *  uploadDocumentAndTrack (res?.docid || res?.data?.id || ...). */
private libraryDocId(d: any): string {
  return d?.id || d?._id || d?.docid || '';
}
private libraryDocName(d: any): string {
  return d?.name || d?.doc_name || d?.document_name || 'Untitled document';
}

openDocumentLibrary(): void {
  this.docLibraryOpen = true;
  this.librarySelection.clear();
  this.docLibrarySearch = '';

  if (!this.iDocuments.length) {
    this.docLibraryLoading = true;
    this.getDocuments();
    // getDocuments() is fire-and-forget (no callback), so poll briefly for
    // the result. Replace with a proper callback/Observable return from
    // getDocuments() if you can — this is a stopgap, not the clean fix.
    const check = setInterval(() => {
      if (this.iDocuments.length || !this.docLibraryLoading) {
        clearInterval(check);
        this.docLibraryLoading = false;
      }
    }, 200);
    setTimeout(() => { clearInterval(check); this.docLibraryLoading = false; }, 6000);
  }
}

closeDocumentLibrary(): void {
  this.docLibraryOpen = false;
}

filteredLibraryDocs(): any[] {
  const term = this.docLibrarySearch.trim().toLowerCase();
  const docs = this.iDocuments || [];
  return term
    ? docs.filter(d => this.libraryDocName(d).toLowerCase().includes(term))
    : docs;
}

isAlreadyAdded(id: string): boolean {
  return this.answers.documents.some(d => d.id === id);
}

isLibrarySelected(id: string): boolean {
  return this.librarySelection.has(id);
}

toggleLibrarySelection(id: string): void {
  this.librarySelection.has(id) ? this.librarySelection.delete(id) : this.librarySelection.add(id);
}

/** Adds the selected library documents into the wizard using the SAME
 *  shape as an already-uploaded document (uploaded: true, id set, no
 *  `file`) — so the existing save flow (renamePendingDocuments /
 *  buildSupportingDocumentsAnswer / patchSupportingDocuments) treats these
 *  exactly like documents uploaded through this step, with no re-upload. */
addSelectedFromLibrary(): void {
  const chosen = (this.iDocuments || []).filter(d => this.librarySelection.has(this.libraryDocId(d)));

  chosen.forEach(d => {
    const id = this.libraryDocId(d);
    const name = this.libraryDocName(d);
    if (!id || this.isAlreadyAdded(id)) { return; }

    this.answers.documents.push({
      id,
      documentType: 'Agreements',        // default per spec — user can change via the dropdown as usual
      documentName: name,
      originalDocumentName: name,
      originalDocumentType: 'Agreements',
      originalOtherCategoryText: '',
      uploaded: true
    });
  });

  this.closeDocumentLibrary();
}
}